import {
  ChangeDetectionStrategy,
  Component,
  SecurityContext,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChildren,
} from '@angular/core';
import { CssDropzone } from '../../../../features/calendar/util/calendar-style.constants';
import {
  SortableContainerCSSClass,
  SortableRowCSSClass,
  SortableRowItemIdAttribute,
  SortedIndexAttribute,
} from '../../../../core/constants-repository/sortable-rows.constants';
import { faEdit, faGridHorizontal, faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { SortableRowsDirective } from '../../../../core/directives/sortable-rows.directive';
import { InvoicePositionModel } from '../../../../core/models/finance/financial-record.model';
import { SortableElementsStorageService } from '../../../../core/services/features/sortable-elements/sortable-elements-storage.service';
import { take } from 'rxjs/operators';
import { SortableEntriesService } from '../../../../core/services/features/sortable-elements/sortable-entries.service';
import { ShiveDialogService } from '../../../../core/services/controls/shive-dialog.service';
import { ShiveAnimationsService } from '../../../../core/services/rendering/shive-animations.service';
import { EditInvoicePositionDialogComponent } from '../../../../pages/finances/invoices/dialogs/edit-invoice-position-dialog/edit-invoice-position-dialog.component';
import { InvoicePositionHandlerService } from '../../../../pages/finances/invoices/invoice-creation-editing/invoice-position-handler.service';

@Component({
  selector: 'app-draggable-financial-records-table',
  templateUrl: './draggable-financial-records-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [ShiveAnimationsService.fadeOnHoverAnimation],
  standalone: false,
})
export class DraggableFinancialRecordsTableComponent {
  // Inputs
  invoicePositions = input.required<InvoicePositionModel[]>();

  // Signals
  readonly currentlyDragging = signal(false);
  readonly draggedRowIndex = signal<number | null>(null);

  // View Children
  renderedInvoicePositions = viewChildren(SortableRowsDirective<InvoicePositionModel>);

  public readonly faPlus = faPlus;
  public readonly faTrash = faTrash;
  public readonly faEdit = faEdit;
  public readonly faGridHorizontal = faGridHorizontal;

  public readonly CssDropzone = CssDropzone;
  public readonly SecurityContext = SecurityContext;

  // Derived invoice totals (react to positions)
  public readonly invoice = computed(() => {
    const positions = this.sortableRowsStorageService.positions();
    const netAmount = this.invoicePositionHandlerService.sumInvoicePositions(positions);
    const taxRate = 20; // could be read from configuration if available
    const taxAmount = this.invoicePositionHandlerService.calculateTaxAmount(
      positions,
      taxRate / 100,
    );
    const totalAmount = netAmount + taxAmount;
    return { netAmount, taxRate, taxAmount, totalAmount };
  });

  public readonly SortableContainerCSSClass = SortableContainerCSSClass;
  public readonly SortableRowCSSClass = SortableRowCSSClass;

  // DI
  public readonly sortableRowsStorageService = inject(SortableElementsStorageService);
  private readonly invoicePositionHandlerService = inject(InvoicePositionHandlerService);
  private readonly sortableRowsService = inject(SortableEntriesService);
  private readonly shiveDialogService = inject(ShiveDialogService);

  constructor() {
    // Load/reset positions when configuration changes
    effect(() => {
      this.sortableRowsStorageService.set(this.invoicePositions());
    });

    // Initialize drag behavior whenever row directives change
    effect(() => {
      const rows = this.renderedInvoicePositions();
      if (rows?.length) {
        this.sortableRowsService.initializeDragBehavior(rows);
      }
    });
  }

  public onTableBodyMouseLeave(): void {
    this.draggedRowIndex.set(null);
  }

  // UI handlers
  public onRowDrag(): void {
    this.currentlyDragging.set(true);
  }

  public onRowDrop(event: { from: number; to: number }): void {
    this.currentlyDragging.set(false);
    this.draggedRowIndex.set(event.to);

    const indexMap = this.buildIndexMap(this.renderedInvoicePositions());
    const sorted = [...this.sortableRowsStorageService.clonedPositions()].sort(
      (a, b) => (indexMap[a.id] ?? 0) - (indexMap[b.id] ?? 0),
    );
    this.sortableRowsStorageService.set(sorted);
  }

  public onRowMouseEnter(rowIndex: number): void {
    this.draggedRowIndex.set(rowIndex);
  }

  public dragPosition(invoicePosition: InvoicePositionModel): void {
    this.sortableRowsService.dispatchMouseDownEvent(invoicePosition);
  }

  public buildDefaultInvoicePosition(id: number): InvoicePositionModel {
    const emptyPosition = new InvoicePositionModel();
    emptyPosition.id = id;
    emptyPosition.description = '';
    emptyPosition.price = 0;
    emptyPosition.timeBlockIds = [];
    return emptyPosition;
  }

  public editPosition(invoicePosition: InvoicePositionModel): void {
    const ref = this.shiveDialogService.open({
      content: EditInvoicePositionDialogComponent,
      width: 572,
      title: 'Position bearbeiten',
    });
    ref.content.setInput('financialRecordPosition', invoicePosition);

    ref.result.pipe(take(1)).subscribe((updated: InvoicePositionModel) => {
      if (updated) {
        this.sortableRowsStorageService.updatePosition(updated);
      }
    });
  }

  private buildIndexMap(
    rows: readonly SortableRowsDirective<InvoicePositionModel>[],
  ): Record<number, number> {
    return rows.reduce<Record<number, number>>((map, rowEl) => {
      const native = rowEl.el.nativeElement as HTMLElement;
      const id = Number(native.getAttribute(SortableRowItemIdAttribute));
      const idx = Number(native.getAttribute(SortedIndexAttribute));
      if (!Number.isNaN(id) && !Number.isNaN(idx)) map[id] = idx;
      return map;
    }, {});
  }
}
